import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/pages/AuthShell";
import GoogleButton from "@/components/GoogleButton";

export default function Register() {
  const navigate = useNavigate();
  const { loginWith } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      loginWith(data.token, data.user);
      toast.success("Account created! Let's set things up.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your space"
      subtitle="A cozy home base for your teaching day — in two minutes."
      footer={<>Already have an account? <Link to="/login" className="text-primary font-semibold hover:underline">Log in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="name" className="text-slate-600">Your name</Label>
          <div className="relative mt-1.5">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
              data-testid="register-name-input" placeholder="Ms. Rivera"
              className="pl-10 rounded-xl bg-slate-50 border-slate-200 h-12 focus:bg-white" />
          </div>
        </div>
        <div>
          <Label htmlFor="email" className="text-slate-600">Email</Label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email-input" placeholder="you@school.edu"
              className="pl-10 rounded-xl bg-slate-50 border-slate-200 h-12 focus:bg-white" />
          </div>
        </div>
        <div>
          <Label htmlFor="password" className="text-slate-600">Password</Label>
          <div className="relative mt-1.5">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password-input" placeholder="At least 6 characters"
              className="pl-10 rounded-xl bg-slate-50 border-slate-200 h-12 focus:bg-white" />
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="register-submit-button"
          className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 text-base">
          {loading ? <Loader2 className="animate-spin" /> : "Create account"}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-6">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-xs text-slate-400">or</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton label="Sign up with Google" />
    </AuthShell>
  );
}
